import type { ReactNode } from "react";
import { Gauge, Layers3, Timer } from "lucide-react";

import type { PredictionResponse } from "../../types/api";
import { Card } from "../ui/Card";

export function PredictionResultCard({
  prediction,
  previewUrl
}: {
  prediction: PredictionResponse | null;
  previewUrl: string | null;
}) {
  return (
    <Card className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-ink-muted">Prediction result</p>
          <h3 className="mt-2 font-display text-2xl font-semibold text-white">Inference output</h3>
        </div>
      </div>

      {prediction ? (
        <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
          <div className="overflow-hidden rounded-[24px] border border-white/8 bg-white/[0.03]">
            {previewUrl ? (
              <img alt="Prediction candidate" className="h-full w-full object-cover" src={previewUrl} />
            ) : (
              <div className="flex min-h-[220px] items-center justify-center text-sm text-ink-muted">No preview</div>
            )}
          </div>
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <Metric icon={<Layers3 className="h-4 w-4" />} label="Label" value={prediction.prediction.label} />
              <Metric
                icon={<Gauge className="h-4 w-4" />}
                label="Confidence"
                value={`${(prediction.prediction.score * 100).toFixed(2)}%`}
              />
              <Metric icon={<Timer className="h-4 w-4" />} label="Latency" value={`${prediction.inference.latency_ms.toFixed(2)} ms`} />
            </div>

            <div className="space-y-3">
              <p className="text-xs uppercase tracking-[0.18em] text-ink-muted">Top predictions</p>
              <div className="space-y-3">
                {prediction.prediction.top_k.map((entry) => (
                  <div key={entry.label} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-white">{entry.label}</span>
                      <span className="text-ink-soft">{(entry.score * 100).toFixed(2)}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-white/10">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-accent to-accent-strong"
                        style={{ width: `${Math.max(6, entry.score * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-[24px] border border-dashed border-white/12 bg-white/[0.02] px-6 py-10 text-center text-sm text-ink-soft">
          Prediction output appears here after you run inference.
        </div>
      )}
    </Card>
  );
}

function Metric({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-[22px] border border-white/8 bg-white/[0.03] p-4">
      <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink-muted">
        {icon}
        {label}
      </div>
      <p className="text-base font-semibold text-white">{value}</p>
    </div>
  );
}
